/**
 * Supabase Client (Web)
 *
 * Re-exports from @prosperia/shared with web-specific extensions.
 * Web-specific features:
 * - Global error handlers for refresh token errors
 * - sessionStorage for pending subscriptions
 */

// Ensure shared package is initialized first
import './sharedInit';

// Re-export everything from shared
export {
  getSupabase,
  isSupabaseInitialized,
  getCurrentUser,
  getCurrentSession,
  signOut,
  getCompanyId,
  clearClientCompanyIdCache,
  hasPermission,
  createCompanyWithOwner,
  addEmployee,
  batchUpdateJobStatus,
  invalidateCache,
  healthCheck,
  createRealtimeSubscription,
  withCompanyFilter,
  batchOperation,
  handleSupabaseError,
} from '@prosperia/shared';

// Import supabase instance from shared
import { getSupabase } from '@prosperia/shared';

// Get the initialized supabase client (browser only)
const supabase = typeof window !== 'undefined' ? getSupabase() : null;

// ============================================
// Web-specific error handlers
// ============================================

if (typeof window !== 'undefined') {
// Add global error handler to catch and suppress refresh token errors
window.addEventListener('error', (event) => {
  const errorMessage = event.error?.message || event.message || '';

  // Check if this is a Supabase refresh token error
  if (
    errorMessage.includes('Invalid Refresh Token') ||
    errorMessage.includes('Refresh Token Not Found')
  ) {
    console.warn('[Supabase] Caught refresh token error, suppressing console output');

    // Prevent the error from showing in console
    event.preventDefault();
    event.stopPropagation();

    // Clear corrupted auth tokens
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.includes('supabase') && key.includes('auth')) {
        localStorage.removeItem(key);
      }
    });

    return false;
  }
});

// Also handle unhandled promise rejections (async errors)
window.addEventListener('unhandledrejection', (event) => {
  const errorMessage = event.reason?.message || '';

  // Check if this is a Supabase refresh token error
  if (
    errorMessage.includes('Invalid Refresh Token') ||
    errorMessage.includes('Refresh Token Not Found')
  ) {
    console.warn('[Supabase] Caught refresh token promise rejection, suppressing');

    // Prevent the error from showing in console
    event.preventDefault();

    // Clear corrupted auth tokens
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.includes('supabase') && key.includes('auth')) {
        localStorage.removeItem(key);
      }
    });
  }
});
} // end if (typeof window !== 'undefined')

// ============================================
// Web-specific functions
// ============================================

/**
 * Create company for user after authentication (web-specific)
 * Uses sessionStorage for pending subscription handling
 */
export const createCompanyForUser = async (user) => {
  try {
    if (!user?.user_metadata?.pending_company_creation) {
      return null;
    }

    console.log('[createCompanyForUser] Creating company for authenticated user:', user.id);

    const companyName = user.user_metadata.company_name || 'My Company';
    const ownerFirstName = user.user_metadata.first_name || '';
    const ownerLastName = user.user_metadata.last_name || '';
    const ownerPhone = user.user_metadata.phone || '';
    const ownerEmail = user.user_metadata.company_email || '';
    const signupSource = user.user_metadata.signup_source || 'website';

    // Use the updated create_company_with_owner function
    const { data: companyId, error: companyError } = await supabase.rpc(
      'create_company_with_owner',
      {
        p_company_name: companyName,
        p_owner_first_name: ownerFirstName,
        p_owner_last_name: ownerLastName,
        p_owner_phone: ownerPhone,
        p_owner_email: ownerEmail,
        p_signup_source: signupSource,
      }
    );

    if (companyError) {
      console.error('[createCompanyForUser] Company creation error:', companyError);
      throw companyError;
    }

    console.log('[createCompanyForUser] Company created successfully:', companyId);

    // Link any pending subscription to the company using email-first approach
    try {
      const userEmail = user.email?.toLowerCase().trim();
      const stripeCustomerId = user.user_metadata.stripe_customer_id;
      console.log('[createCompanyForUser] Looking for pending subscription with email:', userEmail);

      // Primary method: Find pending subscription by email
      let pendingSub = null;
      if (userEmail) {
        const { data: pendingSubData } = await supabase
          .from('pending_subscriptions')
          .select('*')
          .eq('user_email', userEmail)
          .is('company_id', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (pendingSubData) {
          pendingSub = pendingSubData;
          console.log('[createCompanyForUser] Found pending subscription via email:', pendingSub.id);
        }
      }

      // Fallback method: Find by Stripe customer ID if email lookup fails
      if (!pendingSub && stripeCustomerId) {
        const { data: pendingSubData } = await supabase
          .from('pending_subscriptions')
          .select('*')
          .eq('stripe_customer_id', stripeCustomerId)
          .is('company_id', null)
          .single();

        if (pendingSubData) {
          pendingSub = pendingSubData;
          console.log('[createCompanyForUser] Found pending subscription via Stripe customer ID:', pendingSub.id);
        }
      }

      // Final fallback: Check session storage (web-specific)
      if (!pendingSub) {
        const pendingSubscriptionData = sessionStorage.getItem('pending_subscription');
        if (pendingSubscriptionData) {
          pendingSub = JSON.parse(pendingSubscriptionData);
          console.log('[createCompanyForUser] Found pending subscription in session:', pendingSub.id);
        }
      }

      if (pendingSub) {
        // Update the pending subscription with company ID using email-based linking
        const { data: linkSuccess, error: updateError } = await supabase.rpc(
          'link_pending_subscription_by_email',
          {
            p_user_email: userEmail,
            p_company_id: companyId,
          }
        );

        if (updateError) {
          console.error('[createCompanyForUser] Error updating pending subscription:', updateError);
        } else if (linkSuccess) {
          console.log('[createCompanyForUser] Pending subscription linked to company');

          // Subscription rows and billing periods are provisioned by the
          // verified Stripe webhook. Never synthesize paid billing state in
          // the browser: its subscription trigger runs as authenticated and
          // must not bypass the companies billing-column guard (#425).

        }
      } else {
        console.log('[createCompanyForUser] No pending subscription found for user');
      }
    } catch (error) {
      console.error('[createCompanyForUser] Error linking pending subscription:', error);
      // Don't fail company creation if pending subscription linking fails
    }

    // Update user metadata to remove pending flag
    await supabase.auth.updateUser({
      data: {
        ...user.user_metadata,
        company_id: companyId,
        pending_company_creation: false,
      },
    });

    return companyId;
  } catch (error) {
    console.error('[createCompanyForUser] Error creating company for user:', error);
    return null;
  }
};

/**
 * Clear company ID cache (for backward compatibility)
 * Note: The actual implementation is in shared, this is an alias
 */
export { clearClientCompanyIdCache as clearCompanyIdCache } from '@prosperia/shared';

// Also re-export with the camelCase variant for backward compatibility
export const withCompanyFilterCamelCase = (query, companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required for data operations');
  }
  return query.eq('company_id', companyId);
};

// Export the supabase client
export { supabase };
export default supabase;
